"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  Panel,
  SelectField,
  StatCard,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { ColorField } from "@/components/tools/color-charts/color-field";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseColor } from "@/lib/tool-kit/color-charts/color-parse";
import { toRgbString } from "@/lib/tool-kit/color-charts/color-space";
import {
  CHECK_ORDER,
  type CheckId,
  contrastRatio,
  flatten,
  REQUIRED_RATIO,
  type Suggestion,
  suggest,
  truncateRatio,
  wcagChecks,
} from "@/lib/tool-kit/color-charts/contrast";

/** English strings. A translated content file overrides these through `ui`. */
export const COLOR_CONTRAST_CHECKER_STRINGS = {
  foregroundLabel: "Text color (foreground)",
  backgroundLabel: "Background color",
  foregroundPicker: "Pick the text color",
  backgroundPicker: "Pick the background color",
  invalidColor:
    "Not a color this tool can read — try #777777, rgb(119 119 119) or a name such as navy.",
  swap: "Swap text and background",
  results: "Contrast result",
  ratioLabel: "Contrast ratio",
  ratioValue: "{ratio}:1",
  ratioNote:
    "Shown rounded down to two decimals: WCAG does not round a near miss up, so 4.499 fails 4.5.",
  copyRatio: "Copy ratio",
  copied: "Copied!",
  previewLabel: "Preview of the text on the background",
  sampleNormal:
    "Normal text — the quick brown fox jumps over the lazy dog (16 px).",
  sampleLarge: "Large text, 24 px (18 pt)",
  sampleBold: "Large bold text, 18.7 px (14 pt)",
  sampleUi: "Button or icon",
  transparency:
    "A transparent color was flattened first: the background is laid over white, then the text over the background.",
  tableCriterion: "Requirement",
  tableNeeds: "Needs",
  tableResult: "Result",
  normalAA: "Normal text, AA (1.4.3)",
  normalAAA: "Normal text, AAA (1.4.6)",
  largeAA: "Large text, AA (1.4.3)",
  largeAAA: "Large text, AAA (1.4.6)",
  uiAA: "UI components and graphics, AA (1.4.11)",
  needsRatio: "{ratio}:1",
  pass: "Pass",
  fail: "Fail",
  largeDefinition:
    "Large text means at least 18 pt (24 px) regular or 14 pt (about 18.7 px) bold. On slides and posters, body text is usually large; footnotes and captions often are not.",
  suggestionsHeading: "Suggested colors that pass",
  targetLabel: "Aim for",
  targetNormalAA: "Normal text, AA — 4.5:1",
  targetNormalAAA: "Normal text, AAA — 7:1",
  targetLargeAA: "Large text and graphics, AA — 3:1",
  targetLargeAAA: "Large text, AAA — 4.5:1",
  alreadyPasses: "This pair already meets {ratio}:1 — nothing to change.",
  changeText: "Change the text color",
  changeBackground: "Change the background",
  suggestionLine: "{hex} — {ratio}:1",
  use: "Use this",
  swatchLabel: "Suggested color {hex}",
  noneAvailable:
    "No lightness of this color reaches {ratio}:1 against the other one — change the other color instead.",
  suggestionNote:
    "Each suggestion keeps the hue and saturation and changes only the lightness, by the smallest step that passes.",
} as const;

type Strings = typeof COLOR_CONTRAST_CHECKER_STRINGS;
type Target = "normalAA" | "normalAAA" | "largeAA" | "largeAAA";

const TARGETS: readonly Target[] = [
  "normalAA",
  "normalAAA",
  "largeAA",
  "largeAAA",
];

const TARGET_LABEL: Record<Target, keyof Strings> = {
  normalAA: "targetNormalAA",
  normalAAA: "targetNormalAAA",
  largeAA: "targetLargeAA",
  largeAAA: "targetLargeAAA",
};

const DEFAULT_FOREGROUND = "#777777";
const DEFAULT_BACKGROUND = "#ffffff";

function SuggestionCard({
  heading,
  suggestion,
  target,
  strings,
  onUse,
}: {
  heading: string;
  suggestion: Suggestion | null;
  target: number;
  strings: Strings;
  onUse: (hex: string) => void;
}) {
  const fmt = useToolNumber();
  const two = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <p className="font-medium text-foreground text-sm">{heading}</p>
      {suggestion ? (
        <div className="flex flex-wrap items-center gap-3">
          <span
            aria-label={t(strings.swatchLabel, { hex: suggestion.hex })}
            className="block h-10 w-10 rounded-md border border-border"
            role="img"
            style={{ backgroundColor: suggestion.hex }}
          />
          <span className="font-mono text-foreground text-sm">
            {t(strings.suggestionLine, {
              hex: suggestion.hex,
              ratio: fmt(truncateRatio(suggestion.ratio), two),
            })}
          </span>
          <button
            className={BUTTON_SMALL}
            onClick={() => onUse(suggestion.hex)}
            type="button"
          >
            {strings.use}
          </button>
        </div>
      ) : (
        <p className={HINT}>
          {t(strings.noneAvailable, { ratio: fmt(target) })}
        </p>
      )}
    </div>
  );
}

export default function ColorContrastCheckerWidget() {
  const strings = useToolUi(COLOR_CONTRAST_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [foreground, setForeground] = useState(DEFAULT_FOREGROUND);
  const [background, setBackground] = useState(DEFAULT_BACKGROUND);
  const [target, setTarget] = useState<Target>("normalAA");

  const fgParsed = parseColor(foreground);
  const bgParsed = parseColor(background);
  const two = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  let body: ReactNode = null;
  if (fgParsed && bgParsed) {
    const { fg, bg } = flatten(fgParsed, bgParsed);
    const ratio = contrastRatio(fg, bg);
    const checks = wcagChecks(ratio);
    const needed = REQUIRED_RATIO[target];
    const meets = ratio >= needed;
    const fgCss = toRgbString(fg);
    const bgCss = toRgbString(bg);
    const sample = { color: fgCss, backgroundColor: bgCss };
    body = (
      <div aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <div className="grid gap-4 sm:grid-cols-[minmax(0,14rem)_1fr]">
          <div className="space-y-2">
            <StatCard
              emphasis
              label={strings.ratioLabel}
              value={t(strings.ratioValue, {
                ratio: fmt(truncateRatio(ratio), two),
              })}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyRatio}
              small
              text={`${truncateRatio(ratio).toFixed(2)}:1`}
            />
          </div>
          <div
            aria-label={strings.previewLabel}
            className="space-y-2 rounded-lg border border-border p-4"
            role="img"
            style={sample}
          >
            <p className="text-base">{strings.sampleNormal}</p>
            <p className="text-2xl">{strings.sampleLarge}</p>
            <p className="font-bold" style={{ fontSize: "18.667px" }}>
              {strings.sampleBold}
            </p>
            <p
              className="inline-block rounded-md border-2 px-3 py-1 text-sm"
              style={{ borderColor: fgCss }}
            >
              {strings.sampleUi}
            </p>
          </div>
        </div>
        <p className={HINT}>{strings.ratioNote}</p>
        {fgParsed.a < 1 || bgParsed.a < 1 ? (
          <Notice>{strings.transparency}</Notice>
        ) : null}
        <DataTable
          head={[
            strings.tableCriterion,
            strings.tableNeeds,
            strings.tableResult,
          ]}
          rows={CHECK_ORDER.map((id: CheckId) => {
            const check = checks.find((c) => c.id === id);
            return [
              strings[id],
              t(strings.needsRatio, { ratio: fmt(REQUIRED_RATIO[id]) }),
              check?.pass ? strings.pass : strings.fail,
            ];
          })}
        />
        <p className={HINT}>{strings.largeDefinition}</p>
        <Panel title={strings.suggestionsHeading}>
          <SelectField
            id="ccc-target"
            label={strings.targetLabel}
            onChange={setTarget}
            options={TARGETS.map((id) => ({
              value: id,
              label: strings[TARGET_LABEL[id]],
            }))}
            value={target}
          />
          {meets ? (
            <Notice>{t(strings.alreadyPasses, { ratio: fmt(needed) })}</Notice>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <SuggestionCard
                heading={strings.changeText}
                onUse={setForeground}
                strings={strings}
                suggestion={suggest("foreground", fg, bg, needed)}
                target={needed}
              />
              <SuggestionCard
                heading={strings.changeBackground}
                onUse={setBackground}
                strings={strings}
                suggestion={suggest("background", fg, bg, needed)}
                target={needed}
              />
            </div>
          )}
          <p className={HINT}>{strings.suggestionNote}</p>
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
        <ColorField
          fallback={DEFAULT_FOREGROUND}
          id="ccc-foreground"
          invalid={strings.invalidColor}
          label={strings.foregroundLabel}
          onChange={setForeground}
          pickerLabel={strings.foregroundPicker}
          value={foreground}
        />
        <button
          className={`${BUTTON_SECONDARY} sm:mt-7`}
          onClick={() => {
            setForeground(background);
            setBackground(foreground);
          }}
          type="button"
        >
          {strings.swap}
        </button>
        <ColorField
          fallback={DEFAULT_BACKGROUND}
          id="ccc-background"
          invalid={strings.invalidColor}
          label={strings.backgroundLabel}
          onChange={setBackground}
          pickerLabel={strings.backgroundPicker}
          value={background}
        />
      </div>
      {body}
    </div>
  );
}
